import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useConversationHistory } from '../hooks/useConversationHistory.ts'
import { useConversationalMode } from '../hooks/useConversationalMode.ts'
import { useMessageLimits } from '../hooks/useMessageLimits.ts'
import { analyzeWithLLM } from '../services/llmService.ts'
import { loadConversations } from '../utils/conversationStorage.ts'
import type { Message } from '../utils/conversationStorage.ts'

export interface ConversationalAgentContextValue {
  currentSession: ReturnType<typeof useConversationHistory>['currentSession']
  allSessions: ReturnType<typeof useConversationHistory>['allSessions']
  createSession: ReturnType<typeof useConversationHistory>['createSession']
  loadSession: ReturnType<typeof useConversationHistory>['loadSession']
  addMessage: ReturnType<typeof useConversationHistory>['addMessage']
  removeSession: ReturnType<typeof useConversationHistory>['removeSession']
  clearCurrentSession: ReturnType<typeof useConversationHistory>['clearCurrentSession']
  isConversationalModeActive: boolean
  activateConversationalMode: ReturnType<typeof useConversationalMode>['activate']
  deactivateConversationalMode: ReturnType<typeof useConversationalMode>['deactivate']
  canSendMessage: boolean
  remainingMessages: number
  hasReachedLimit: boolean
  limit: number
  sendMessage: (message: string) => Promise<void>
  isProcessing: boolean
  userId: string | undefined
  userData: any
}

const ConversationalAgentContext = createContext<ConversationalAgentContextValue | null>(null)

export function useConversationalAgent(): ConversationalAgentContextValue {
  const value = useContext(ConversationalAgentContext)
  if (!value) {
    throw new Error('useConversationalAgent must be used within ConversationalAgentProvider')
  }
  return value
}

export function useConversationalAgentOptional(): ConversationalAgentContextValue | null {
  return useContext(ConversationalAgentContext)
}

interface ConversationalAgentProviderProps {
  children: React.ReactNode
}

export function ConversationalAgentProvider({ children }: ConversationalAgentProviderProps) {
  const location = useLocation()
  const userData = useSelector((state: any) => state.general?.userData)
  const isUserPremium = useSelector((state: any) => state.general?.isUserPremium ?? false)
  const userId = userData?.id

  const {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    addMessage,
    removeSession,
    clearCurrentSession
  } = useConversationHistory(userId)

  const {
    isActive: isConversationalModeActive,
    activate: activateConversationalMode,
    deactivate: deactivateConversationalMode
  } = useConversationalMode()

  const { canSendMessage, remainingMessages, hasReachedLimit, limit } = useMessageLimits(
    currentSession,
    isUserPremium
  )

  const [isProcessing, setIsProcessing] = useState(false)

  useEffect(() => {
    const pathname = location.pathname
    const fromConversation = (location.state as any)?.fromConversation === true
    const sessionIdFromUrl = new URLSearchParams(location.search).get('session')
    if (pathname !== '/dashboard' && !fromConversation && !sessionIdFromUrl) {
      clearCurrentSession()
    }
  }, [location.pathname, (location.state as any)?.fromConversation, location.search])

  const sendMessage = useCallback(
    async (message: string) => {
      if (!canSendMessage) return

      let session = currentSession
      if (!session) {
        session = createSession()
      }

      const userMessage: Message = {
        id: `msg-${Date.now()}-user`,
        role: 'user',
        content: message,
        timestamp: new Date().toISOString()
      }

      addMessage(userMessage)
      setIsProcessing(true)

      await new Promise((resolve) => setTimeout(resolve, 100))

      try {
        const conversations = loadConversations(userId)
        const currentSessionForLLM =
          conversations.sessions.find(
            (s) => s.sessionId === (currentSession?.sessionId || session!.sessionId)
          ) || session!

        const allMessages = currentSessionForLLM.messages || []
        const messagesWithoutCurrent =
          allMessages.length > 0 && allMessages[allMessages.length - 1].id === userMessage.id
            ? allMessages.slice(0, -1)
            : allMessages
        const previousMessages = messagesWithoutCurrent.slice(-14)
        const historyForLLM = previousMessages.map((m) => ({
          role: m.role,
          content:
            m.role === 'assistant' && m.llmRawResponse ? m.llmRawResponse : m.content
        }))

        const llmResult = await analyzeWithLLM(message, historyForLLM)

        const llmRawResponseObj: any = {
          actionId: llmResult.actionId,
          confidence: llmResult.confidence,
          response: llmResult.response,
          guideMode: llmResult.guideMode || false
        }

        if (llmResult.needsData && llmResult.data) {
          llmRawResponseObj.needsData = true
          llmRawResponseObj.data = llmResult.data
          llmRawResponseObj.total = llmResult.total || 0
          if ((llmResult as any).quotationType)
            llmRawResponseObj.quotationType = (llmResult as any).quotationType
          if ((llmResult as any).workVisibility)
            llmRawResponseObj.workVisibility = (llmResult as any).workVisibility
          if ((llmResult as any).isPublic !== undefined)
            llmRawResponseObj.isPublic = (llmResult as any).isPublic
          if (llmResult.noResultsMessage)
            llmRawResponseObj.noResultsMessage = llmResult.noResultsMessage
          if ((llmResult as any).searchTerm)
            llmRawResponseObj.searchTerm = (llmResult as any).searchTerm
          if ((llmResult as any).projectType)
            llmRawResponseObj.projectType = (llmResult as any).projectType
        }

        const llmRawResponse = JSON.stringify(llmRawResponseObj)

        const assistantMessage: Message = {
          id: `msg-${Date.now()}-assistant`,
          role: 'assistant',
          content: llmResult.response,
          timestamp: new Date().toISOString(),
          actionId: llmResult.actionId || null,
          guideMode: llmResult.guideMode || false,
          llmRawResponse,
          ...(llmResult.needsData &&
            llmResult.data && {
              data: llmResult.data,
              dataTotal: llmResult.total || 0,
              needsData: true,
              quotationType: (llmResult as any).quotationType,
              workVisibility: (llmResult as any).workVisibility,
              isPublic: (llmResult as any).isPublic,
              noResultsMessage: llmResult.noResultsMessage
            })
        }

        addMessage(assistantMessage)
      } catch (error) {
        const errorMsg: Message = {
          id: `msg-${Date.now()}-error`,
          role: 'assistant',
          content:
            'Disculpá, hubo un problema procesando tu consulta. ¿Podrías intentar de nuevo?',
          timestamp: new Date().toISOString()
        }
        addMessage(errorMsg)
      } finally {
        setIsProcessing(false)
      }
    },
    [
      canSendMessage,
      currentSession,
      createSession,
      addMessage,
      userId
    ]
  )

  const value: ConversationalAgentContextValue = {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    addMessage,
    removeSession,
    clearCurrentSession,
    isConversationalModeActive,
    activateConversationalMode,
    deactivateConversationalMode,
    canSendMessage,
    remainingMessages,
    hasReachedLimit,
    limit,
    sendMessage,
    isProcessing,
    userId,
    userData
  }

  return (
    <ConversationalAgentContext.Provider value={value}>
      {children}
    </ConversationalAgentContext.Provider>
  )
}
